import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getAuthHeaders } from '../../auth';
import { usePermissions } from '../../services/PermissionsContext';
import ConfirmDialog from '../common/ConfirmDialog';
import { openFilePreview } from '../common/FilePreviewModal';
import useSortableTable from '../common/useSortableTable';
import PopoutWindow from '../common/PopoutWindow';
import TableScrollContainer from '../common/TableScrollContainer';
import JobForm from '../documentation/JobForm';
import ShippingLinePayablesForm from '../admin/ShippingLinePayablesForm';

const REPORT_API = '/api/admin/shipping-line-payables/deposit-report/';
const REMARK_API = id => `/api/admin/shipping-line-payables/${id}/deposit-remark/`;
const REFUND_API = id => `/api/admin/shipping-line-payables/${id}/deposit-refund/`;
const APPROVE_API = id => `/api/admin/shipping-line-payables/${id}/deposit-approve/`;
const UNAPPROVE_API = id => `/api/admin/shipping-line-payables/${id}/deposit-unapprove/`;
const REJECT_API = id => `/api/admin/shipping-line-payables/${id}/deposit-reject/`;
const START_DATE_API = '/api/admin/shipping-line-payables/deposit-start-date/';
const JOB_API = id => `/api/documentation/jobs/${id}/`;
const PAYABLE_API = id => `/api/admin/shipping-line-payables/${id}/`;

const TABS = [
    { id: 'PENDING', label: 'PENDING' },
    { id: 'SUBMITTED', label: 'SUBMITTED' },
    { id: 'RECEIVED', label: 'RECEIVED' },
    { id: 'APPROVED', label: 'APPROVED' },
];

const fmtDate = v => (v ? new Date(v).toLocaleDateString('en-GB') : '-');
const fmtDateTime = v => (v
    ? new Date(v).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '-');
const fmtAmount = v => Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const daysSince = v => {
    if (!v) return '-';
    const diff = Math.floor((Date.now() - new Date(v).getTime()) / 86400000);
    return diff < 0 ? 0 : diff;
};

const previewAttachment = async (url, name) => {
    try {
        const res = await fetch(url, { headers: getAuthHeaders(true) });
        if (!res.ok) throw new Error('Failed to load document');
        const blob = await res.blob();
        // Blob URLs carry no filename — derive an extension so the preview
        // renders the file instead of offering a download.
        const ext = {
            'application/pdf': 'pdf',
            'image/png': 'png', 'image/jpeg': 'jpg', 'image/gif': 'gif',
            'image/webp': 'webp', 'image/bmp': 'bmp', 'image/svg+xml': 'svg',
        }[blob.type] || (url.match(/\.([A-Za-z0-9]+)(?:[?#]|$)/) || [])[1] || '';
        const displayName = ext && name && !name.toLowerCase().endsWith(`.${ext}`)
            ? `${name}.${ext}` : name;
        const blobUrl = URL.createObjectURL(blob);
        const previewWindow = openFilePreview(blobUrl, displayName);
        const revoke = () => URL.revokeObjectURL(blobUrl);
        if (previewWindow) previewWindow.addEventListener('beforeunload', revoke);
        setTimeout(revoke, 60000);
    } catch (e) {
        toast.error('Could not open document preview.');
    }
};

const fileLink = (url, label = 'View') => (
    <a
        href="#"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); previewAttachment(url, label); }}
        style={{ fontSize: 11 }}
    >
        {label}
    </a>
);

const daysStyles = `
@keyframes days-flash-orange { 0%, 49% { color: #e69500; } 50%, 100% { color: #333; } }
@keyframes days-flash-red { 0%, 49% { color: red; } 50%, 100% { color: #333; } }
.days-since-orange { font-weight: bold; animation: days-flash-orange 1s step-start infinite; }
.days-since-red { font-weight: bold; animation: days-flash-red 1s step-start infinite; }
.deposit-report-table tbody td { text-align: center; }
`;

const sortValue = (row, key) => {
    switch (key) {
        case 'amount':
        case 'refund_amount':
            return row[key] != null && row[key] !== '' ? Number(row[key]) : null;
        case 'days_since': {
            const d = daysSince(row.refund_submitted_date);
            return d === '-' ? null : d;
        }
        case 'created_at':
        case 'payment_date':
        case 'released_date':
        case 'refund_submitted_date':
        case 'refund_received_date':
            return row[key] ? new Date(row[key]).getTime() : null;
        case 'approved':
            return row.refund_approved_at ? new Date(row.refund_approved_at).getTime() : null;
        case 'rejection_reason':
            return row.deposit_rejection_reason;
        default:
            return row[key];
    }
};

const daysSinceCell = (v, readOnly = false) => {
    const days = daysSince(v);
    if (days === '-') return '-';
    const cls = !readOnly && days >= 5 ? 'days-since-red' : !readOnly && days >= 3 ? 'days-since-orange' : null;
    return <span className={cls || undefined}>{days}</span>;
};

const DepositReport = ({ onRowsLoaded }) => {
    const { hasPermission } = usePermissions();
    const canEdit = hasPermission('SHIPPING REPORTS', 'can_edit');
    const canApprove = hasPermission('SHIPPING REPORTS', 'can_delete');

    const [rows, setRows] = useState([]);
    const [remarkOptions, setRemarkOptions] = useState([]);
    const [startDate, setStartDate] = useState(null);
    const [startDateDraft, setStartDateDraft] = useState('');
    const [savingStartDate, setSavingStartDate] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('PENDING');
    const [savingId, setSavingId] = useState(null);
    const [submitModal, setSubmitModal] = useState(null); // { row, date, file }
    const [refundDrafts, setRefundDrafts] = useState({}); // { payableId: { date, amount, file } }
    const [amountEditing, setAmountEditing] = useState({}); // { payableId: true }
    const [confirmApprove, setConfirmApprove] = useState(null); // row
    const [confirmUnapprove, setConfirmUnapprove] = useState(null); // row
    const [rejectModal, setRejectModal] = useState(null); // { row, reason }
    const [jobModal, setJobModal] = useState({ isOpen: false, job: null });
    const [payablePopouts, setPayablePopouts] = useState([]); // { key, payable }

    const fetchRows = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const res = await fetch(REPORT_API, { headers: getAuthHeaders(false) });
            if (!res.ok) throw new Error('Failed to load deposit report.');
            const data = await res.json();
            const list = data.rows || [];
            setRows(list);
            setRemarkOptions(data.remark_options || ['REFUND SUBMITTED']);
            setStartDate(data.start_date || null);
            setStartDateDraft(data.start_date || '');
            if (onRowsLoaded) onRowsLoaded(list);
        } catch (e) {
            toast.error(e.message || 'Failed to load deposit report.');
        } finally {
            setLoading(false);
        }
    }, [onRowsLoaded]);

    useEffect(() => { fetchRows(); }, [fetchRows]);

    const postForm = async (url, formData) => {
        const res = await fetch(url, { method: 'POST', headers: getAuthHeaders(true), body: formData });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || 'Save failed.');
        }
    };

    const handleRemarkChange = async (row, value) => {
        if (value === 'REFUND SUBMITTED') {
            setSubmitModal({ row, date: row.refund_submitted_date || '', file: null });
            return;
        }
        setSavingId(row.payable_id);
        try {
            const fd = new FormData();
            fd.append('remark', value);
            await postForm(REMARK_API(row.payable_id), fd);
            toast.success('Remark saved.');
            fetchRows(true);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSavingId(null);
        }
    };

    const saveRefundSubmitted = async () => {
        const { row, date, file } = submitModal;
        if (!date) { toast.error('Refund Submitted Date is required.'); return; }
        if (!file && !row.refund_letter_url) { toast.error('Refund Letter attachment is required.'); return; }
        setSavingId(row.payable_id);
        try {
            const fd = new FormData();
            fd.append('remark', 'REFUND SUBMITTED');
            fd.append('refund_submitted_date', date);
            if (file) fd.append('refund_letter', file);
            await postForm(REMARK_API(row.payable_id), fd);
            toast.success('Refund submission saved.');
            setSubmitModal(null);
            fetchRows(true);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSavingId(null);
        }
    };

    const getRefundDraft = id => refundDrafts[id] || { date: '', amount: '', file: null };
    const setRefundDraft = (id, patch) =>
        setRefundDrafts(prev => ({ ...prev, [id]: { ...getRefundDraft(id), ...(prev[id] || {}), ...patch } }));

    const saveRefundAmount = async row => {
        const draft = getRefundDraft(row.payable_id);
        const amount = draft.amount !== '' ? draft.amount : (row.refund_amount != null ? String(row.refund_amount) : String(row.amount || ''));
        if (amount === '') { toast.error('Refund Amount is required.'); return; }
        setSavingId(row.payable_id);
        try {
            const fd = new FormData();
            fd.append('refund_amount', amount);
            await postForm(REFUND_API(row.payable_id), fd);
            toast.success('Refund amount saved.');
            setRefundDrafts(prev => { const n = { ...prev }; delete n[row.payable_id]; return n; });
            setAmountEditing(prev => ({ ...prev, [row.payable_id]: false }));
            fetchRows(true);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSavingId(null);
        }
    };

    const saveRefund = async row => {
        const draft = getRefundDraft(row.payable_id);
        const date = draft.date || row.refund_received_date || '';
        if (!date) { toast.error('Refund Received Date is required.'); return; }
        if (!draft.file && !row.refund_proof_url) { toast.error('Refund Proof document is required.'); return; }
        setSavingId(row.payable_id);
        try {
            const fd = new FormData();
            fd.append('refund_received_date', date);
            if (draft.file) fd.append('refund_proof', draft.file);
            const amount = draft.amount !== '' ? draft.amount : (row.refund_amount || row.amount || '');
            if (amount !== '') fd.append('refund_amount', amount);
            await postForm(REFUND_API(row.payable_id), fd);
            toast.success('Refund details saved.');
            setRefundDrafts(prev => { const n = { ...prev }; delete n[row.payable_id]; return n; });
            fetchRows(true);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSavingId(null);
        }
    };

    const approveRow = async row => {
        setConfirmApprove(null);
        setSavingId(row.payable_id);
        try {
            const res = await fetch(APPROVE_API(row.payable_id), { method: 'POST', headers: getAuthHeaders(true) });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Approval failed.');
            }
            toast.success('Refund approved.');
            fetchRows(true);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSavingId(null);
        }
    };

    const unapproveRow = async row => {
        setConfirmUnapprove(null);
        setSavingId(row.payable_id);
        try {
            const res = await fetch(UNAPPROVE_API(row.payable_id), { method: 'POST', headers: getAuthHeaders(true) });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Unapprove failed.');
            }
            toast.success('Refund unapproved.');
            fetchRows(true);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSavingId(null);
        }
    };

    const rejectRow = async () => {
        const { row, reason } = rejectModal;
        if (!reason.trim()) { toast.error('Rejection reason is required.'); return; }
        setSavingId(row.payable_id);
        try {
            const fd = new FormData();
            fd.append('reason', reason.trim());
            await postForm(REJECT_API(row.payable_id), fd);
            toast.success('Refund rejected.');
            setRejectModal(null);
            fetchRows(true);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSavingId(null);
        }
    };

    const saveSubmittedTo = async (row, checked) => {
        setSavingId(row.payable_id);
        try {
            const fd = new FormData();
            fd.append('refund_submitted_to', checked ? 'K & K' : '');
            await postForm(REFUND_API(row.payable_id), fd);
            fetchRows(true);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSavingId(null);
        }
    };

    const saveStartDate = async () => {
        setSavingStartDate(true);
        try {
            const res = await fetch(START_DATE_API, {
                method: 'POST',
                headers: { ...getAuthHeaders(false), 'Content-Type': 'application/json' },
                body: JSON.stringify({ start_date: startDateDraft }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to save start date.');
            }
            toast.success('Start date saved.');
            fetchRows(true);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSavingStartDate(false);
        }
    };

    const openJobModal = async row => {
        if (!row.job_id) return;
        try {
            const res = await fetch(JOB_API(row.job_id), { headers: getAuthHeaders(false) });
            if (!res.ok) throw new Error('Failed to load job details.');
            setJobModal({ isOpen: true, job: await res.json() });
        } catch (e) {
            toast.error(e.message);
        }
    };

    const openPayablePopout = async row => {
        try {
            const res = await fetch(PAYABLE_API(row.payable_id), { headers: getAuthHeaders(false) });
            if (!res.ok) throw new Error('Failed to load payable details.');
            const payable = await res.json();
            setPayablePopouts(prev => [
                ...prev,
                { key: `${row.payable_id}_${Date.now()}`, payable },
            ]);
        } catch (e) {
            toast.error(e.message);
        }
    };

    const closePayablePopout = key =>
        setPayablePopouts(prev => prev.filter(p => p.key !== key));

    const rowTab = r => {
        const s = r.deposit_status || 'PENDING';
        if (s === 'SUBMITTED' && r.refund_received_date && Number(r.refund_amount) > 0 && !r.deposit_rejection_reason) return 'RECEIVED';
        return s;
    };

    const visibleRows = rows.filter(r => rowTab(r) === activeTab);
    const { items: sortedVisibleRows, requestSort, getSortIndicator } = useSortableTable(
        visibleRows, { key: 'created_at', direction: 'ascending' }, sortValue, activeTab
    );

    const Th = ({ k, children }) => (
        <th onClick={() => requestSort(k)} style={{ cursor: 'pointer', whiteSpace: 'nowrap', textAlign: 'center' }}>
            {children} {getSortIndicator(k)}
        </th>
    );

    const remarkCell = r => {
        if (!r.eir_returned_any) {
            return <span>{r.deposit_remark || 'WAITING INTERCHANGE'}</span>;
        }
        if (!canEdit) {
            return <span>{r.deposit_remark || 'WAITING INTERCHANGE'}</span>;
        }
        return (
            <select
                value={r.deposit_remark || 'WAITING INTERCHANGE'}
                disabled={savingId === r.payable_id}
                onChange={e => { if (e.target.value) handleRemarkChange(r, e.target.value); }}
                style={{ fontSize: 12 }}
            >
                {!r.deposit_remark && <option value="WAITING INTERCHANGE">WAITING INTERCHANGE</option>}
                {r.deposit_remark && !remarkOptions.includes(r.deposit_remark) && r.deposit_remark !== 'WAITING INTERCHANGE' && (
                    <option value={r.deposit_remark}>{r.deposit_remark}</option>
                )}
                {remarkOptions.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
        );
    };

    const baseCells = r => (
        <>
            <td>{fmtDate(r.created_at)}</td>
            <td>
                {r.job_id ? (
                    <a href="#" onClick={e => { e.preventDefault(); e.stopPropagation(); openJobModal(r); }}>
                        {r.idf_no || '-'}
                    </a>
                ) : (r.idf_no || '-')}
            </td>
            <td>
                <a href="#" onClick={e => { e.preventDefault(); e.stopPropagation(); openPayablePopout(r); }}>
                    {r.bl_no || '-'}
                </a>
            </td>
            <td>{r.shipping_line || '-'}</td>
            <td style={{ fontWeight: 'bold' }}>{r.customer || '-'}</td>
            <td>{r.container_size || '-'}</td>
            <td>{r.currency || '-'}</td>
            <td style={{ textAlign: 'right' }}>{fmtAmount(r.amount)}</td>
            <td>{fmtDate(r.payment_date)}</td>
            <td>{r.transporter || '-'}</td>
        </>
    );

    const submittedDateCell = (r, readOnly) => (
        <td>
            {fmtDate(r.refund_submitted_date)}
            {r.refund_letter_url && <div>{fileLink(r.refund_letter_url, 'Refund Letter')}</div>}
        </td>
    );

    const refundReceivedCell = (r, readOnly) => {
        if (readOnly) {
            return (
                <td>
                    {fmtDate(r.refund_received_date)}
                    {r.refund_proof_url && <div>{fileLink(r.refund_proof_url, 'Refund Proof')}</div>}
                </td>
            );
        }
        const draft = getRefundDraft(r.payable_id);
        return (
            <td>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                    <input
                        type="date"
                        value={draft.date || r.refund_received_date || ''}
                        onChange={e => setRefundDraft(r.payable_id, { date: e.target.value })}
                        style={{ fontSize: 12, width: 110 }}
                        disabled={!canEdit || savingId === r.payable_id}
                    />
                    <input
                        type="file"
                        onChange={e => setRefundDraft(r.payable_id, { file: e.target.files[0] || null })}
                        style={{ fontSize: 11, maxWidth: 150 }}
                        disabled={!canEdit || savingId === r.payable_id}
                    />
                    {r.refund_proof_url && <div>{fileLink(r.refund_proof_url, 'Refund Proof')}</div>}
                    {canEdit && (
                        <button
                            type="button"
                            className="dialog-btn dialog-btn-primary"
                            style={{ padding: '2px 8px', fontSize: 11, alignSelf: 'flex-start' }}
                            disabled={savingId === r.payable_id}
                            onClick={() => saveRefund(r)}
                        >
                            {savingId === r.payable_id ? 'Saving...' : 'SAVE'}
                        </button>
                    )}
                </div>
            </td>
        );
    };

    const refundAmountCell = (r, readOnly) => {
        if (readOnly) {
            return (
                <td style={{ textAlign: 'right' }}>
                    {r.refund_amount != null ? fmtAmount(r.refund_amount) : (r.amount != null ? fmtAmount(r.amount) : '-')}
                    {r.refund_submitted_to && (
                        <div style={{ textAlign: 'left', fontSize: 11 }}>Submitted to: {r.refund_submitted_to}</div>
                    )}
                </td>
            );
        }
        const draft = getRefundDraft(r.payable_id);
        const amountEditable = !!(r.refund_received_date && r.refund_proof_url);
        const inReceivedTab = activeTab === 'RECEIVED';
        const isEditing = !!amountEditing[r.payable_id];
        const displayAmount = r.refund_amount != null ? r.refund_amount : (r.amount != null ? r.amount : null);
        return (
            <td>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                    {isEditing && canEdit && amountEditable ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={draft.amount}
                                onChange={e => setRefundDraft(r.payable_id, { amount: e.target.value.replace(/[^0-9.]/g, '') })}
                                style={{ fontSize: 12, width: 90, textAlign: 'right' }}
                                disabled={savingId === r.payable_id}
                                autoFocus
                            />
                            <button
                                type="button"
                                className="icon-btn"
                                title={savingId === r.payable_id ? 'Saving...' : 'Save'}
                                disabled={savingId === r.payable_id}
                                onClick={() => saveRefundAmount(r)}
                            >
                                <i className={savingId === r.payable_id ? 'fa fa-spinner fa-spin' : 'fa fa-save'} />
                            </button>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 12 }}>{displayAmount != null ? fmtAmount(displayAmount) : '-'}</span>
                            {canEdit && (
                                <button
                                    type="button"
                                    className="icon-btn"
                                    title={amountEditable ? 'Edit refund amount' : 'Save Refund Received Date and Refund Proof first'}
                                    disabled={!amountEditable || savingId === r.payable_id}
                                    onClick={() => {
                                        setRefundDraft(r.payable_id, { amount: displayAmount != null ? String(displayAmount) : '' });
                                        setAmountEditing(prev => ({ ...prev, [r.payable_id]: true }));
                                    }}
                                >
                                    <i className="fa fa-pencil" />
                                </button>
                            )}
                        </div>
                    )}
                    {inReceivedTab && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, whiteSpace: 'nowrap' }}>
                            <span>Refund Submitted To:</span>
                            <input
                                type="checkbox"
                                checked={r.refund_submitted_to === 'K & K'}
                                disabled={!canEdit || savingId === r.payable_id}
                                onChange={e => saveSubmittedTo(r, e.target.checked)}
                            />
                            <span>K &amp; K</span>
                        </label>
                    )}
                </div>
            </td>
        );
    };

    const approvedCell = (r, readOnly) => {
        if (readOnly) {
            return (
                <td>
                    {r.refund_approved_by && <div>{r.refund_approved_by}</div>}
                    <div>{fmtDateTime(r.refund_approved_at)}</div>
                </td>
            );
        }
        const approvable = Number(r.refund_amount) > 0;
        return (
            <td style={{ textAlign: 'center' }}>
                {canApprove ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                        <button
                            type="button"
                            className="dialog-btn dialog-btn-primary"
                            style={{ padding: '2px 10px', fontSize: 11 }}
                            disabled={!approvable || savingId === r.payable_id}
                            title={approvable ? 'Approve refund' : 'Refund Amount must be greater than 0 before approving'}
                            onClick={() => setConfirmApprove(r)}
                        >
                            APPROVE
                        </button>
                        <button
                            type="button"
                            className="dialog-btn"
                            style={{ padding: '2px 10px', fontSize: 11, color: '#b30000' }}
                            disabled={savingId === r.payable_id}
                            title="Reject refund (requires a reason)"
                            onClick={() => setRejectModal({ row: r, reason: '' })}
                        >
                            REJECT
                        </button>
                    </div>
                ) : null}
            </td>
        );
    };

    const isSubmittedView = activeTab !== 'PENDING';
    const readOnly = activeTab === 'APPROVED';

    return (
        <div style={{ marginTop: 16, overflowX: 'auto' }}>
            <style>{daysStyles}</style>
            <div style={{ fontWeight: 'bold', background: '#e6f7ff', padding: 8, borderRadius: 6, marginBottom: 8 }}>
                DEPOSIT / REFUND REPORT
                {startDate && (
                    <span style={{ fontWeight: 'normal', fontSize: 12, marginLeft: 12 }}>
                        (Showing payables created from {fmtDate(startDate)})
                    </span>
                )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
                <div className="custom-tabs-container" style={{ marginBottom: 0 }}>
                    {TABS.map(t => (
                        <div
                            key={t.id}
                            className={`custom-tab ${activeTab === t.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(t.id)}
                        >
                            {t.label} ({rows.filter(r => rowTab(r) === t.id).length})
                        </div>
                    ))}
                </div>
                {canApprove && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>START DATE:</label>
                        <input
                            type="date"
                            value={startDateDraft}
                            onChange={e => setStartDateDraft(e.target.value)}
                            style={{ fontSize: 12 }}
                            disabled={savingStartDate}
                        />
                        <button
                            type="button"
                            className="dialog-btn dialog-btn-primary"
                            style={{ padding: '3px 10px', fontSize: 11 }}
                            onClick={saveStartDate}
                            disabled={savingStartDate}
                        >
                            {savingStartDate ? 'Saving...' : 'SAVE'}
                        </button>
                    </div>
                )}
            </div>
            <TableScrollContainer>
                <table className="jobs-table deposit-report-table" style={{ textAlign: 'center', overflow: 'visible' }}>
                    <thead>
                        <tr>
                            <Th k="created_at">CREATED</Th>
                            <Th k="idf_no">IDF</Th>
                            <Th k="bl_no">BL</Th>
                            <Th k="shipping_line">SHIPPING LINE</Th>
                            <Th k="customer">CUSTOMER</Th>
                            <Th k="container_size">CONTAINER SIZE</Th>
                            <Th k="currency">CURR</Th>
                            <Th k="amount">AMOUNT</Th>
                            <Th k="payment_date">PAID ON</Th>
                            <Th k="transporter">TRANSPORTER</Th>
                            {activeTab === 'PENDING' && <Th k="deposit_remark">REMARKS</Th>}
                            {isSubmittedView && (
                                <>
                                    <Th k="released_date">RELEASED ON</Th>
                                    <Th k="refund_submitted_date">SUBMITTED DATE</Th>
                                    <Th k="days_since">DAYS</Th>
                                    <Th k="refund_received_date">REFUND RECEIVED DATE</Th>
                                    <Th k="refund_amount">REFUND AMOUNT</Th>
                                    {activeTab === 'SUBMITTED' && <Th k="rejection_reason">REJECTION REASON</Th>}
                                    {activeTab === 'SUBMITTED' && <Th k="deposit_remark">REMARKS</Th>}
                                    {activeTab !== 'SUBMITTED' && <Th k="approved">APPROVED</Th>}
                                    {activeTab === 'APPROVED' && <th style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>ACTION</th>}
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={20} style={{ textAlign: 'center' }}>Loading...</td></tr>
                        )}
                        {!loading && visibleRows.length === 0 && (
                            <tr><td colSpan={20} className="no-data">No {activeTab.toLowerCase()} deposit records found.</td></tr>
                        )}
                        {!loading && sortedVisibleRows.map(r => (
                            <tr key={r.payable_id}>
                                {baseCells(r)}
                                {activeTab === 'PENDING' && <td>{remarkCell(r)}</td>}
                                {isSubmittedView && (
                                    <>
                                        <td>{fmtDate(r.released_date)}</td>
                                        {submittedDateCell(r, readOnly)}
                                        <td style={{ textAlign: 'center' }}>{daysSinceCell(r.refund_submitted_date, readOnly)}</td>
                                        {refundReceivedCell(r, readOnly)}
                                        {refundAmountCell(r, readOnly)}
                                        {activeTab === 'SUBMITTED' && <td style={{ color: 'red', textTransform: 'uppercase' }}>{r.deposit_rejection_reason || '-'}</td>}
                                        {activeTab === 'SUBMITTED' && <td>{remarkCell(r)}</td>}
                                        {activeTab !== 'SUBMITTED' && approvedCell(r, readOnly)}
                                        {activeTab === 'APPROVED' && (
                                            <td style={{ textAlign: 'center' }}>
                                                {canApprove ? (
                                                    <button
                                                        type="button"
                                                        className="dialog-btn"
                                                        style={{ padding: '2px 10px', fontSize: 11, color: '#b30000' }}
                                                        disabled={savingId === r.payable_id}
                                                        title="Unapprove refund (moves the record back)"
                                                        onClick={() => setConfirmUnapprove(r)}
                                                    >
                                                        UNAPPROVE
                                                    </button>
                                                ) : '-'}
                                            </td>
                                        )}
                                    </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </TableScrollContainer>

            {submitModal && (
                <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setSubmitModal(null); }}>
                    <div className="dialog-container" style={{ width: 420 }}>
                        <div className="dialog-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3>Refund Submitted</h3>
                            <button type="button" className="icon-btn" onClick={() => setSubmitModal(null)}>
                                <i className="fa fa-times"></i>
                            </button>
                        </div>
                        <div className="dialog-body">
                            <div style={{ marginBottom: 12 }}>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Refund Submitted Date *</label>
                                <input
                                    type="date"
                                    value={submitModal.date}
                                    onChange={e => setSubmitModal(m => ({ ...m, date: e.target.value }))}
                                    style={{ fontSize: 13 }}
                                />
                            </div>
                            <div style={{ marginBottom: 12 }}>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Refund Letter *</label>
                                <input
                                    type="file"
                                    onChange={e => setSubmitModal(m => ({ ...m, file: e.target.files[0] || null }))}
                                    style={{ fontSize: 12 }}
                                />
                                {submitModal.row.refund_letter_url && (
                                    <div style={{ marginTop: 4 }}>{fileLink(submitModal.row.refund_letter_url, 'Current Refund Letter')}</div>
                                )}
                            </div>
                        </div>
                        <div className="dialog-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '10px 12px' }}>
                            <button type="button" className="dialog-btn" onClick={() => setSubmitModal(null)}>Cancel</button>
                            <button
                                type="button"
                                className="dialog-btn dialog-btn-primary"
                                onClick={saveRefundSubmitted}
                                disabled={savingId === submitModal.row.payable_id}
                            >
                                {savingId === submitModal.row.payable_id ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {rejectModal && (
                <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setRejectModal(null); }}>
                    <div className="dialog-container" style={{ width: 420 }}>
                        <div className="dialog-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3>Reject Refund</h3>
                            <button type="button" className="icon-btn" onClick={() => setRejectModal(null)}>
                                <i className="fa fa-times"></i>
                            </button>
                        </div>
                        <div className="dialog-body">
                            <div style={{ marginBottom: 12 }}>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Rejection Reason *</label>
                                <textarea
                                    value={rejectModal.reason}
                                    onChange={e => setRejectModal(m => ({ ...m, reason: e.target.value }))}
                                    rows={3}
                                    style={{ width: '100%', fontSize: 13, boxSizing: 'border-box' }}
                                />
                            </div>
                        </div>
                        <div className="dialog-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '10px 12px' }}>
                            <button type="button" className="dialog-btn" onClick={() => setRejectModal(null)}>Cancel</button>
                            <button
                                type="button"
                                className="dialog-btn dialog-btn-primary"
                                onClick={rejectRow}
                                disabled={savingId === rejectModal.row.payable_id}
                            >
                                {savingId === rejectModal.row.payable_id ? 'Saving...' : 'Reject'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={!!confirmApprove}
                title="Confirm Approval"
                message={confirmApprove ? `Approve refund for ${confirmApprove.reference_no || `payable #${confirmApprove.payable_id}`}? This will move the record to the Approved tab.` : ''}
                onConfirm={() => approveRow(confirmApprove)}
                onCancel={() => setConfirmApprove(null)}
            />

            <ConfirmDialog
                open={!!confirmUnapprove}
                title="Confirm Unapprove"
                message={confirmUnapprove ? `Unapprove refund for ${confirmUnapprove.reference_no || `payable #${confirmUnapprove.payable_id}`}? This will move the record out of the Approved tab.` : ''}
                onConfirm={() => unapproveRow(confirmUnapprove)}
                onCancel={() => setConfirmUnapprove(null)}
            />

            {jobModal.isOpen && (
                <div className="modal-overlay" onClick={() => setJobModal({ isOpen: false, job: null })}>
                    <div
                        className="dialog-container"
                        onClick={e => e.stopPropagation()}
                        style={{
                            width: '95%',
                            maxWidth: 1400,
                            height: '95vh',
                            display: 'flex',
                            flexDirection: 'column',
                            margin: 'auto',
                        }}
                    >
                        <div className="dialog-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3>Job Details (View Only)</h3>
                            <button type="button" className="icon-btn" onClick={() => setJobModal({ isOpen: false, job: null })} title="Close">
                                <i className="fa fa-times" />
                            </button>
                        </div>
                        <div style={{ overflowY: 'auto', flex: 1, padding: '0 20px 20px' }}>
                            <JobForm
                                mode="view"
                                initialData={jobModal.job}
                                onClose={() => setJobModal({ isOpen: false, job: null })}
                                documentsEditable={false}
                            />
                        </div>
                    </div>
                </div>
            )}

            {payablePopouts.map(p => (
                <PopoutWindow
                    key={p.key}
                    title="View Shipping Line Payable"
                    name={`deposit_payable_view_${p.key}`}
                    toastContainerId={`deposit-payable-toast-${p.key}`}
                    onClose={() => closePayablePopout(p.key)}
                >
                    <ShippingLinePayablesForm
                        mode="view"
                        initialData={p.payable}
                        onClose={() => closePayablePopout(p.key)}
                        onSave={() => closePayablePopout(p.key)}
                        module="SD"
                        popoutMode={true}
                        toastContainerId={`deposit-payable-toast-${p.key}`}
                    />
                </PopoutWindow>
            ))}
        </div>
    );
};

export default DepositReport;
